import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, of, switchMap, map, catchError, throwError } from 'rxjs';
import { CryptoService } from '../services/crypto.service';
import { environment } from '../../../environments/environment';

export const encryptionInterceptor: HttpInterceptorFn = (req, next) => {
  const cryptoService = inject(CryptoService);

  // Check if encryption is disabled globally or per-request
  const skipEncryption = req.headers.get('X-Skip-Encryption') === 'true' || !environment.enableEncryption;
  const isTargetApi = req.url.startsWith(environment.apiUrl);

  if (skipEncryption || !isTargetApi) {
    return next(req);
  }

  // Skip request body encryption for FormData / binary file uploads
  const isFormData = req.body instanceof FormData;

  // Encrypt request body to plain encrypted text string alone
  const prepareRequest$ = () => {
    if (!isFormData && req.body && (req.method === 'POST' || req.method === 'PUT' || req.method === 'PATCH')) {
      return from(cryptoService.encryptString(req.body)).pipe(
        map((encryptedText: string) => {
          return req.clone({
            body: encryptedText,
            setHeaders: {
              'Content-Type': 'text/plain',
              'X-Encrypted-Request': 'true'
            }
          });
        })
      );
    }
    return of(req);
  };

  return prepareRequest$().pipe(
    switchMap((modifiedReq) =>
      next(modifiedReq).pipe(
        switchMap((event) => {
          // Decrypt response body if event is HttpResponse and body is present
          if (event instanceof HttpResponse && event.body) {
            const body = event.body as any;
            let payloadToDecrypt: any = null;
            let isNestedData = false;
            let isNestedEncryptedData = false;

            if (typeof body === 'string' && body.trim().length > 0) {
              payloadToDecrypt = body;
            } else if (typeof body === 'object' && body !== null) {
              if ('data' in body && typeof body.data === 'string') {
                payloadToDecrypt = body.data;
                isNestedData = true;
              } else if ('encryptedData' in body && typeof body.encryptedData === 'string') {
                payloadToDecrypt = body.encryptedData;
                isNestedEncryptedData = true;
              }
            }

            if (payloadToDecrypt) {
              return from(cryptoService.decrypt(payloadToDecrypt)).pipe(
                map((decryptedData) => {
                  if (isNestedData && typeof body === 'object' && body !== null) {
                    if (typeof decryptedData === 'object' && decryptedData !== null && 'success' in decryptedData && 'data' in decryptedData) {
                      return event.clone({ body: decryptedData });
                    }
                    return event.clone({
                      body: {
                        ...body,
                        data: decryptedData
                      }
                    });
                  }
                  if (isNestedEncryptedData && typeof body === 'object' && body !== null) {
                    return event.clone({
                      body: {
                        ...body,
                        data: decryptedData
                      }
                    });
                  }
                  return event.clone({ body: decryptedData });
                }),
                catchError((err) => {
                  console.warn('Response decryption fallback triggered:', err);
                  return of(event);
                })
              );
            }
          }
          return of(event);
        }),
        catchError((error: HttpErrorResponse) => {
          // Decrypt/parse error body if API returns an HttpErrorResponse (e.g. 400, 401, 422, 500)
          if (error && error.error) {
            const rawError = error.error;
            let payloadToDecrypt: any = null;

            if (typeof rawError === 'string' && rawError.trim().length > 0) {
              payloadToDecrypt = rawError;
            } else if (typeof rawError === 'object' && rawError !== null) {
              if ('data' in rawError && typeof rawError.data === 'string') {
                payloadToDecrypt = rawError.data;
              } else if ('encryptedData' in rawError && typeof rawError.encryptedData === 'string') {
                payloadToDecrypt = rawError.encryptedData;
              }
            }

            const decryptOrParseError$ = () => {
              if (payloadToDecrypt) {
                return from(cryptoService.decrypt(payloadToDecrypt)).pipe(
                  map((decrypted) => {
                    let parsed: any = decrypted;
                    if (typeof decrypted === 'string') {
                      try {
                        parsed = JSON.parse(decrypted);
                      } catch {
                        parsed = { message: decrypted, error: decrypted };
                      }
                    }
                    return parsed;
                  }),
                  catchError(() => {
                    if (typeof rawError === 'string') {
                      try {
                        return of(JSON.parse(rawError));
                      } catch {
                        return of({ message: rawError, error: rawError });
                      }
                    }
                    return of(rawError);
                  })
                );
              } else if (typeof rawError === 'string') {
                try {
                  return of(JSON.parse(rawError));
                } catch {
                  return of({ message: rawError, error: rawError });
                }
              }
              return of(rawError);
            };

            return decryptOrParseError$().pipe(
              switchMap((parsedErrorBody) => {
                const clonedError = new HttpErrorResponse({
                  error: parsedErrorBody,
                  headers: error.headers,
                  status: error.status,
                  statusText: error.statusText,
                  url: error.url || undefined
                });
                return throwError(() => clonedError);
              })
            );
          }
          return throwError(() => error);
        })
      )
    )
  );
};
